import { Badge } from '@roger/ui-components';
import { formatDateInAgencyZone } from '../../utils/dateUtils';

/**
 * Canonical tooltip for the imported-chart marker. Takes the import timestamp
 * so the badge dates itself wherever it appears; falls back to a dateless
 * sentence for a payload that did not carry one. Not exported: react-refresh
 * wants component files exporting components only, and every surface renders
 * the badge rather than the sentence.
 *
 * `imported_at` is a `timestamp with time zone` written as a UTC instant, so it
 * is converted before it is read as a day. The calendar-date helpers split the
 * string at the "T" and keep the UTC date, which reads a day late for any import
 * that ran after a US afternoon, the after-hours window a cutover run tends
 * to land in, and exactly the date an agency reconciles against its own log.
 */
function importedPatientBadgeTitle(importedAt?: string | null): string {
  const when = importedAt ? ` on ${formatDateInAgencyZone(importedAt)}` : '';
  return `Imported from External EMR${when}. Records carry their source values until a clinician reviews them.`;
}

interface ImportedPatientBadgeProps {
  /** Import timestamp for the tooltip (ISO string from the API). */
  importedAt?: string | null;
  /** Layout-only classes for the call site (e.g. `shrink-0` inside a flex row). */
  className?: string;
}

/**
 * Single source of truth for the imported-chart marker — label, styling, and
 * tooltip. Render conditionally on the surface's badge flag (`import_source`
 * set and `import_badge_hidden_at` unset, or the denormalized
 * `show_imported_badge`); this component does not gate itself, so the caller
 * stays in control of when the marker appears — including honoring demotion.
 */
export function ImportedPatientBadge({
  importedAt,
  className,
}: ImportedPatientBadgeProps) {
  return (
    <Badge
      variant='secondary'
      className={className}
      title={importedPatientBadgeTitle(importedAt)}
    >
      Imported
    </Badge>
  );
}
